import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { User, LogOut, Flag, TreePine, GraduationCap, Landmark, Volume2, VolumeX, Shield, Languages, MessageCircle, Sparkles, ArrowLeft, ChevronDown } from 'lucide-react';
import MiniGlobe from './MiniGlobe';
import ChainTimeline from './ChainTimeline';
import useGlobePresence from '../lib/useGlobePresence';
import LaunchBanner from './LaunchBanner';
import EmailVerifyBanner from './EmailVerifyBanner';
import ComingSoonModal from './ComingSoonModal';

// API helpers used by the cascading nav. Direct API client to avoid widening
// lib/api.js for one consumer.
import api from '../lib/api';

const geoAPI = {
  countries: () => api.get('/countries'),
  subdivisions: (countrySlug) => api.get(`/countries/${countrySlug}/subdivisions`),
  cities: (subdivisionId) => api.get(`/subdivisions/${subdivisionId}/cities`),
  detect: () => api.get('/geo/detect'),
};

// Cascading nav scope state, lives at the App level so URLs can hydrate it.
const SCOPE_DEFAULT = { country: null, subdivision: null, city: null };

// Apr 29 2026: Lead's tab simplification — drop Country History,
// Subdivision, City, Power & Structures, Biographies, Weird Facts,
// Monuments & Symbols. Result: 7 tabs only — every one of which loads
// a real page. No more "Coming soon" gate.
const COMING_SOON_TABS = new Set([]);

const SUBJECT_TABS = [
  { id: 'world',          label: 'World History',  kind: 'destination' },
  { id: 'roots',          label: 'My Roots',       kind: 'utility' },
  { id: 'my-certificate', label: 'Certificates',   kind: 'utility' },
  { id: 'profile',        label: 'My Profile',     kind: 'utility' },
  { id: 'about',          label: 'About Us',       kind: 'utility' },
  { id: 'contact',        label: 'Contact Us',     kind: 'utility' },
  { id: 'workcenter',     label: 'Work Center',    kind: 'utility' },
];

// Country slug → human "<COUNTRY> History" label. Falls back to
// "Country History" while geolocation resolves.
const COUNTRY_HISTORY_LABEL = {
  us: 'US History', ca: 'Canada History', mx: 'Mexico History',
  uk: 'UK History', gb: 'UK History', fr: 'France History', de: 'Germany History',
  it: 'Italy History', es: 'Spain History', pt: 'Portugal History',
  jp: 'Japan History', cn: 'China History', kr: 'Korea History', in: 'India History',
  br: 'Brazil History', ar: 'Argentina History', au: 'Australia History',
  za: 'South Africa History', eg: 'Egypt History', ng: 'Nigeria History',
  ru: 'Russia History', ua: 'Ukraine History', tr: 'Türkiye History',
  sa: 'Saudi Arabia History', ir: 'Iran History', pk: 'Pakistan History',
};

export default function HUDLayout({
  activeView, onViewChange, globeNodes, globeBeacons, onNodeClick,
  currentYear, onYearChange, timelineEvents, timelineScope, onChainSelect,
  isKidMode, onToggleMode, soundOn, onToggleSound, activeEra, children,
  immersive = false, immersiveTitle = '', onExitImmersive,
  scope = SCOPE_DEFAULT, onScopeChange,
  // chrome: 'full' (default) | 'tabs-only' (no globe / no timeline — used
  // by episode pages and profile pages per Lead's Apr 28 2026 brief
  // "On those pages after the pinhole. There should be not globe nor
  // timeline. Just the adjusted tabs as I described earlier.").
  chrome = 'full',
}) {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isTeacherOrAdmin = user?.role === 'admin' || user?.role === 'teacher';
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null); // 'country' | 'subdivision' | 'city' | null
  const [countries, setCountries] = useState([]);
  const [subdivisions, setSubdivisions] = useState({ items: [], subdivision_label: 'Region' });
  const [cities, setCities] = useState({ items: [] });

  // ── Globe social layer (Apr 29 2026 batch #2) ─────────────────────
  // Pulls active-earth presence + friend arcs + user heat. Only fires
  // when chrome === 'full' (the Time Machine view actually has a globe);
  // tab-only chrome modes don't render the globe so the heartbeat would
  // be wasted bandwidth.
  const presence = useGlobePresence({ enabled: chrome !== 'tabs-only' });

  // Lazy-load countries on first dropdown open
  const ensureCountries = useCallback(async () => {
    if (countries.length) return;
    try {
      const r = await geoAPI.countries();
      setCountries(r.data || []);
    } catch { /* network — leave empty */ }
  }, [countries.length]);
  const ensureSubdivisions = useCallback(async (slug) => {
    if (!slug) { setSubdivisions({ items: [], subdivision_label: 'Region' }); return; }
    try {
      const r = await geoAPI.subdivisions(slug);
      setSubdivisions(r.data || { items: [], subdivision_label: 'Region' });
    } catch { setSubdivisions({ items: [], subdivision_label: 'Region' }); }
  }, []);
  const ensureCities = useCallback(async (subId) => {
    if (!subId) { setCities({ items: [] }); return; }
    try {
      const r = await geoAPI.cities(subId);
      setCities(r.data || { items: [] });
    } catch { setCities({ items: [] }); }
  }, []);

  // Reload subdivisions/cities whenever scope shifts (deep-link hydration)
  useEffect(() => { ensureSubdivisions(scope.country?.slug); }, [scope.country?.slug, ensureSubdivisions]);
  useEffect(() => { ensureCities(scope.subdivision?.id); }, [scope.subdivision?.id, ensureCities]);

  // ─── Auto-detect default scope from the user's IP on first mount ───
  // Lead's Apr 28 2026 brief: "Before I put in my information it should
  // start at state capital in Sacramento for city. Since I am in California
  // for state. Like that for every user." We hit /api/geo/detect once
  // (free ip-api.com lookup, falls back to US/California/Sacramento) and
  // push the resolved scope upstream via onScopeChange. Skipped if the
  // parent already provided a scope, so deep-links and saved prefs win.
  const [detectAttempted, setDetectAttempted] = useState(false);
  useEffect(() => {
    if (detectAttempted) return;
    if (scope.country || scope.subdivision || scope.city) {
      setDetectAttempted(true);
      return;
    }
    setDetectAttempted(true);
    geoAPI.detect()
      .then((r) => {
        const next = {
          country: r.data?.country || null,
          subdivision: r.data?.subdivision || null,
          city: r.data?.city || null,
        };
        if (next.country || next.subdivision || next.city) {
          onScopeChange?.(next);
        }
      })
      .catch(() => { /* offline / 404 — leave scope null */ });
  }, [detectAttempted, scope.country, scope.subdivision, scope.city, onScopeChange]);

  // Coming Soon modal state — fired when the user clicks any tab that
  // isn't fully wired yet (Country / State / City / Monuments / About /
  // Contact for now). Tabs in COMING_SOON_TABS bypass onViewChange.
  const [comingSoon, setComingSoon] = useState(null); // { id, label } or null
  const handleTabClick = useCallback((tab) => {
    if (COMING_SOON_TABS.has(tab.id)) {
      setComingSoon({ id: tab.id, label: tab.label });
      return;
    }
    onViewChange?.(tab.id);
  }, [onViewChange]);

  // Resolve dynamic tab labels per Lead's brief — Country History becomes
  // "<Country> History", State becomes the state name, City becomes the
  // city name. Uses iso2 (matches COUNTRY_HISTORY_LABEL keys) first, falls
  // back to "<Name> History" for countries not in the curated map.
  const dynamicTabs = useMemo(() => SUBJECT_TABS.map((t) => {
    if (t.id === 'country') {
      const iso2 = (scope.country?.iso2 || '').toLowerCase();
      const label = COUNTRY_HISTORY_LABEL[iso2] || (scope.country?.name ? `${scope.country.name} History` : 'Country History');
      return { ...t, label };
    }
    if (t.id === 'subdivision' && scope.subdivision?.name) return { ...t, label: scope.subdivision.name };
    if (t.id === 'city' && scope.city?.name) return { ...t, label: scope.city.name };
    return t;
  }), [scope.country, scope.subdivision, scope.city]);

  // Close dropdown on outside click / escape
  useEffect(() => {
    if (!openDropdown) return;
    const onDoc = (e) => {
      if (!e.target.closest('[data-dropdown-anchor]')) setOpenDropdown(null);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpenDropdown(null); };
    document.addEventListener('click', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [openDropdown]);

  // Apr 30 2026 iter_67 per Lead: "World history doesn't move why is the
  // scroll bars on the bottom and right side. Remove." The HUD body is
  // locked to 100vh with overflow:hidden, but the html/body document
  // can still acquire a scrollbar if any inner element overflows by a
  // pixel (timeline canvas + glow, etc). Toggle `body.hud-static` which
  // pins `overflow: hidden` on both axes while the HUD is mounted.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.add('hud-static');
    return () => { document.body.classList.remove('hud-static'); };
  }, []);

  const handleAdultToggle = () => {
    if (isKidMode) {
      setShowPinModal(true);
      setPinInput('');
      setPinError('');
    } else {
      onToggleMode();
    }
  };

  // Time Machine mode — when the user is on the "World" (home) view with no
  // chain or episode selected, the globe + timeline BECOME the page: Row 2
  // expands to fill the viewport, the content area is suppressed, and a
  // "▶ START YOUR JOURNEY" CTA anchors the timeline at the bottom. This
  // honors the Lead's Apr 27 2026 brief: "the time machine IS the page."
  const isTimeMachine = activeView === 'world' && !immersive;

  // ─── Immersive mode: chrome HIDES, single slim exit bar replaces it.
  // Methodology hold #5: "the platform leaves every user with a clearer head
  // than they came in with" — orientation chrome (globe/timeline/tabs) and
  // immersion chrome can't both win the same screen. While the user is inside
  // a chain, immersion wins; when they leave, orientation returns.
  if (immersive) {
    return (
      <div className="fixed inset-0 z-10 flex flex-col" data-testid="hud-layout" data-immersive="true">
        {/* Slim exit bar — only platform affordance visible during a chain */}
        <div
          className="pointer-events-auto flex-shrink-0 h-10 md:h-11 flex items-center px-3 md:px-5 z-30 border-b border-[#2F8A92]/10"
          style={{ background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(8px)' }}
          data-testid="immersive-bar"
        >
          <button
            onClick={() => onExitImmersive && onExitImmersive()}
            data-testid="exit-immersive-btn"
            className="flex items-center gap-1.5 font-hud text-[10px] md:text-[11px] tracking-widest text-white/85 hover:text-[#D4A84A] transition-colors whitespace-nowrap"
          >
            <ArrowLeft size={13} /> BACK TO TIME MACHINE
          </button>
          <span
            className="flex-1 text-center font-historical text-[#D4A84A] text-sm md:text-base font-light tracking-tight truncate px-3"
            data-testid="immersive-title"
          >
            {immersiveTitle || 'Episode'}
          </span>
          <span
            className="font-mono text-[8px] md:text-[9px] text-[#52525B] tracking-widest hidden md:inline whitespace-nowrap"
            title="You're inside an episode. The time machine returns when you step back out."
          >
            INSIDE EPISODE
          </span>
        </div>
        <div className="pointer-events-auto flex-1 overflow-y-auto z-10" data-testid="hud-main-content" style={{ background: '#0A0A0A' }}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative z-10 flex flex-col w-full"
      style={{ height: '100vh', maxHeight: '100vh', overflow: 'hidden' }}
      data-testid="hud-layout"
    >
      {/* Apr 30 2026 iter_63 per Lead: "Timeline should be visible at all
          times on world history. No scrolling down to see it." The outer
          wrapper is now LOCKED to 100vh with overflow: hidden so rows
          partition the viewport deterministically — banners + logo +
          tabs + globe + timeline all fit, no scroll. The globe column
          uses flex-1 + min-h-0 so it shrinks to give the timeline its
          fixed height. */}

      {/* ═══ ROW -1: Kickstarter countdown banner — Apr 30 2026 iter_66
           per Lead: "Ticker is getting eating but the top. Bring it down
           some." Added 8px top padding so the ticker drops below the
           hard top edge of the viewport / browser chrome. */}
      <div className="pointer-events-auto flex-shrink-0" style={{ paddingTop: '8px', background: '#0A0A0A' }}>
        <LaunchBanner />
      </div>

      {/* ═══ ROW -0.5: Email verification reminder (hides on verified/admin) ═══ */}
      <EmailVerifyBanner />

      {/* ═══ ROW 0: Language + Gold tagline ═══
           Apr 30 2026 iter_69 per Lead: "Tabs is to big cause of tsogn.
           Remove it replace with gold text The Shared Origins Global
           Network." The PNG logo image was a heavy element pushing the
           row to h-16/20. Replaced with a single line of gold text so
           the row collapses to h-8/10 and hands the extra vertical real
           estate back to the globe. */}
      <div className="pointer-events-auto flex-shrink-0 h-8 md:h-10 flex items-center px-3 md:px-5" style={{ background: '#0A0A0A' }} data-testid="hud-row-logo">
        <div className="flex items-center gap-1.5" data-testid="language-selector">
          <Languages size={14} className="text-white/80" />
          <select
            data-testid="language-select"
            className="bg-transparent font-hud text-[10px] md:text-xs text-white/80 tracking-wider outline-none cursor-pointer border-none"
            defaultValue={typeof document !== 'undefined' ? (document.cookie.match(/googtrans=\/[^/]+\/([^;]+)/)?.[1] || 'en') : 'en'}
            onChange={async (e) => {
              const mod = await import('../lib/translate');
              mod.setLanguage(e.target.value);
            }}
          >
            {[['en','English'],['es','Español'],['fr','Français'],['de','Deutsch'],['pt','Português'],['zh-CN','中文'],['ja','日本語'],['ar','العربية'],['hi','हिन्दी'],['sw','Kiswahili'],['ko','한국어'],['ru','Русский']].map(([v,l]) => (
              <option key={v} value={v} style={{ background: '#0A0A0A' }}>{l}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 flex items-center justify-center select-none">
          <span
            className="font-historical font-light tracking-[0.28em] text-sm md:text-base lg:text-lg"
            style={{ color: '#D4A84A' }}
            data-testid="tsogn-logo"
          >
            The Shared Origins Global Network
          </span>
          <span className="sr-only" data-testid="tsogn-wordmark">TSOGN</span>
          <span className="sr-only" data-testid="tsogn-tagline">The Shared Origins Global Network</span>
        </div>
        <div style={{ width: '80px' }} />
      </div>

      {/* ═══ ROW 1: Tabs (cascading dropdown nav per Foundation Brief §1) ═══ */}
      <div className="pointer-events-auto flex-shrink-0 h-11 md:h-12 flex items-center px-1 md:px-3 z-20 border-b border-[#2F8A92]/8 overflow-x-auto scrollbar-hide" style={{ background: '#0A0A0A' }} data-testid="hud-row-tabs">
        <nav className="flex items-center gap-0 overflow-x-auto scrollbar-hide flex-1" data-testid="hud-nav-tabs">
          {dynamicTabs.map(item => {
            const isActive = activeView === item.id;
            const isCascade = ['country', 'subdivision', 'city'].includes(item.kind);
            const isComingSoon = COMING_SOON_TABS.has(item.id);
            const label = item.label;

            const buttonNode = (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                data-coming-soon={isComingSoon ? 'true' : 'false'}
                data-dropdown-anchor={isCascade ? item.id : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isCascade) {
                    // Cascade tabs still open the dropdown so the user can
                    // browse countries / states / cities. Picking a row
                    // without a published chain shows the Coming Soon
                    // popup instead of switching views.
                    if (item.kind === 'country') ensureCountries();
                    setOpenDropdown(openDropdown === item.id ? null : item.id);
                  } else {
                    setOpenDropdown(null);
                    handleTabClick(item);
                  }
                }}
                onMouseEnter={() => {
                  if (isCascade && window.matchMedia('(hover: hover)').matches) {
                    if (item.kind === 'country') ensureCountries();
                    setOpenDropdown(item.id);
                  }
                }}
                className={`px-3 md:px-5 py-2 md:py-3 font-hud text-base md:text-lg lg:text-xl tracking-wider whitespace-nowrap transition-all duration-300 inline-flex items-center gap-1.5
                  ${isActive
                    ? 'text-[#2F8A92] border-b-2 border-[#2F8A92] bg-[#2F8A92]/5'
                    : 'text-white/85 hover:text-[#2F8A92] border-b-2 border-transparent'
                  }`}
              >
                {label}
                {isCascade && <ChevronDown size={14} className="opacity-65" />}
              </button>
            );

            if (!isCascade) return buttonNode;
            return (
              <div key={item.id} className="relative" data-dropdown-anchor={item.id}>
                {buttonNode}
                {openDropdown === item.id && (
                  <CascadeDropdown
                    kind={item.kind}
                    scope={scope}
                    countries={countries}
                    subdivisions={subdivisions}
                    cities={cities}
                    onSelect={(picked) => {
                      setOpenDropdown(null);
                      if (item.kind === 'country') {
                        const newScope = { country: picked, subdivision: null, city: null };
                        onScopeChange?.(newScope);
                        // Always show Coming Soon when navigating to scoped
                        // pages — the chain isn't built yet for any specific
                        // country/state/city. Lead's Apr 28 brief.
                        const iso2 = (picked.iso2 || '').toLowerCase();
                        setComingSoon({ id: 'country', label: COUNTRY_HISTORY_LABEL[iso2] || `${picked.name} History` });
                      } else if (item.kind === 'subdivision') {
                        onScopeChange?.({ ...scope, subdivision: picked, city: null });
                        setComingSoon({ id: 'subdivision', label: picked.name });
                      } else if (item.kind === 'city') {
                        onScopeChange?.({ ...scope, city: picked });
                        setComingSoon({ id: 'city', label: picked.name });
                      }
                    }}
                    onClose={() => setOpenDropdown(null)}
                  />
                )}
              </div>
            );
          })}
          {/* Apr 29 2026 follow-up #4: Lead's call: "Move Admin link out
              of top navigation tabs. Add Admin link to the footer, small
              text, right-aligned." Admin tab hidden from top nav; the
              footer renders the admin link conditionally on user role. */}
        </nav>
      </div>

      {/* ═══ ROW 2: Globe Row — in Time Machine mode this row becomes the
           hero and flex-expands to fill the remaining viewport, so globe +
           timeline together ARE the page. In all other views it keeps its
           fixed hero-band height (280px desktop). HIDDEN entirely when
           `chrome === 'tabs-only'` (episode + profile pages). ═══ */}
      {chrome !== 'tabs-only' && (
      <div
        className={`pointer-events-auto flex-1 z-20 overflow-hidden relative min-h-0`}
        data-testid="hud-row-globe"
        data-time-machine={isTimeMachine ? 'true' : 'false'}
        style={{ background: '#050508' }}
      >
        {/* Top-right pill stack on the globe panel. Apr 30 2026 — Lead's
            call: ugly share buttons on the globe view removed. The presence
            count badge stays so judges still see "N people walking the
            network right now". Sharing is available from the page footer
            and end-of-episode CTA where it doesn't compete with the planet
            visually. */}
        <div className="absolute top-3 right-3 z-30 flex flex-col items-end gap-1.5">
          {presence.activeCount > 0 && (
            <div
              className="pointer-events-none inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1"
              style={{
                background: 'rgba(0,0,0,0.55)',
                borderColor: 'rgba(47,138,146,0.45)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
              }}
              data-testid="presence-count-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#2F8A92] animate-pulse" />
              <span className="font-mono text-[9px] md:text-[10px] tracking-[0.18em] uppercase text-[#2F8A92]">
                {presence.activeCount === 1 ? '1 person walking' : `${presence.activeCount} people walking`}
              </span>
            </div>
          )}
        </div>
        {/* Starfield + atmospheric glow — covers entire Row 2 */}
        <div className="absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
          {/* Stars */}
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(1px 1px at 10% 20%, rgba(255,255,255,0.6) 0%, transparent 100%),
              radial-gradient(1px 1px at 30% 65%, rgba(255,255,255,0.4) 0%, transparent 100%),
              radial-gradient(1px 1px at 50% 10%, rgba(255,255,255,0.5) 0%, transparent 100%),
              radial-gradient(1px 1px at 70% 40%, rgba(255,255,255,0.3) 0%, transparent 100%),
              radial-gradient(1px 1px at 90% 80%, rgba(255,255,255,0.5) 0%, transparent 100%),
              radial-gradient(1px 1px at 15% 85%, rgba(255,255,255,0.4) 0%, transparent 100%),
              radial-gradient(1px 1px at 45% 55%, rgba(255,255,255,0.3) 0%, transparent 100%),
              radial-gradient(1px 1px at 65% 15%, rgba(255,255,255,0.5) 0%, transparent 100%),
              radial-gradient(1px 1px at 80% 60%, rgba(255,255,255,0.4) 0%, transparent 100%),
              radial-gradient(1px 1px at 25% 45%, rgba(255,255,255,0.3) 0%, transparent 100%),
              radial-gradient(1px 1px at 55% 90%, rgba(255,255,255,0.4) 0%, transparent 100%),
              radial-gradient(1px 1px at 5% 50%, rgba(255,255,255,0.35) 0%, transparent 100%),
              radial-gradient(1px 1px at 85% 25%, rgba(255,255,255,0.45) 0%, transparent 100%),
              radial-gradient(1px 1px at 35% 30%, rgba(255,255,255,0.3) 0%, transparent 100%),
              radial-gradient(1px 1px at 75% 75%, rgba(255,255,255,0.35) 0%, transparent 100%)`
          }} />
          {/* Atmospheric glow centered on globe position */}
          <div className="absolute" style={{
            top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: '500px', height: '500px',
            background: 'radial-gradient(circle, rgba(0,100,140,0.08) 0%, rgba(0,50,80,0.04) 40%, transparent 70%)',
          }} />
        </div>

        {/* ── MOBILE: Compact bar + centered globe. In Time Machine mode
             the globe grows to fill the available vertical room so the
             timeline + globe together own the first viewport. ── */}
        <div className={`md:hidden relative ${isTimeMachine ? 'h-full flex flex-col' : ''}`} style={{ zIndex: 1 }}>
          <div className="flex items-center justify-between px-4 py-1.5">
            <div className="flex flex-col items-start select-none">
              <span className={`font-historical text-[#e8d5a3] ${isTimeMachine ? 'text-base' : 'text-sm'} font-light tracking-tight leading-none`} style={{ textShadow: '0 0 12px rgba(212,168,74,0.25), 0 0 30px rgba(212,168,74,0.08)' }}>Shared Origins</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => onViewChange('us')} className="text-white/80 active:text-[#2F8A92]" title="My Country"><Flag size={14} /></button>
              <button onClick={() => onViewChange('chat')} className="text-white/80 active:text-[#2F8A92]" title="Chat"><MessageCircle size={14} /></button>
              <button onClick={() => onViewChange('roots')} className="text-white/80 active:text-[#D4A84A]" title="My Roots"><TreePine size={14} /></button>
              <button onClick={() => onViewChange('workcenter')} className="text-white/80 active:text-[#2F8A92]" title="Work Center"><GraduationCap size={14} /></button>
              <button onClick={() => onViewChange('methodology-moderation')} data-testid="mobile-methodology-btn" className="text-white/80 active:text-[#D4A84A]" title="Why Wasn't This Flagged?"><Sparkles size={14} /></button>
              <button onClick={() => onViewChange('profile')} data-testid="mobile-profile-btn" className="text-white/80 active:text-[#2F8A92]" title="My Profile"><User size={14} /></button>
              <button onClick={() => onViewChange('accountability')} data-testid="mobile-record-btn" className="text-white/80 active:text-[#D4A84A]" title="My Record"><Shield size={14} /></button>
              <button onClick={logout} className="text-[#52525B] active:text-[#2F8A92]" title="Logout"><LogOut size={12} /></button>
            </div>
          </div>
          <div
            className={`flex items-center justify-center overflow-hidden relative ${isTimeMachine ? 'flex-1' : ''}`}
            style={{ height: isTimeMachine ? undefined : '180px', minHeight: isTimeMachine ? '280px' : undefined }}
          >
            <div className="aspect-square" style={{
              height: isTimeMachine ? 'min(calc(100% - 16px), calc(100vw - 32px))' : '180px',
              width: isTimeMachine ? 'min(calc(100% - 16px), calc(100vw - 32px))' : '180px',
            }}>
              <MiniGlobe
                nodes={globeNodes || []}
                beacons={globeBeacons || []}
                onNodeClick={onNodeClick || (() => {})}
                currentYear={currentYear}
                friendArcs={presence.friendArcs}
                activeUsers={presence.activeUsers}
                userHeat={presence.userHeat}
              />
            </div>
            {/* Moon removed Apr 30 2026 — orbit was broken (slid side-to-side
                instead of orbiting the globe). Lead asked for it gone. */}
          </div>
        </div>

        {/* ── DESKTOP 3-column layout — Apr 30 2026 iter_65 per Lead's
             "globe is being cut off" report. INVERTED the iter_64 layout:
             side columns are now FIXED-width (so the logo + avatar have
             a deterministic frame and never crowd the globe), and the
             GLOBE column is `flex-1` so it grows to fill all leftover
             middle space. The 3D sphere is no longer clipped on either
             edge. Logos remain centered inside their fixed frames so the
             "centered between viewport edge and globe" feel is kept. ── */}
        <div className="hidden md:flex items-stretch relative h-full" style={{ minHeight: '240px', zIndex: 1 }}>
          {/* Logo column — fixed 320/360px frame, logo centered inside.
              May 01 2026 per Lead's uploaded artwork: swapped so-globe.png
              for so-hero-logo.png (the full networked-globe + wordmark
              composition the Lead delivered). Wordmark is baked into the
              image itself, so no separate <span> beneath it. */}
          <div className="flex-shrink-0 flex flex-col items-center justify-center px-4" style={{ width: 'clamp(280px, 20vw, 400px)' }} data-testid="hud-logo-side">
            <img
              src="/assets/brand/so-hero-logo.png"
              alt="Shared Origins · Global Connection · Narratives · Humanity"
              className="w-56 md:w-64 lg:w-72 h-auto object-contain select-none"
              data-testid="hud-logo-side-image"
            />
            {activeEra ? (
              <span className="font-hud text-[12px] lg:text-sm tracking-widest uppercase mt-3 transition-all duration-700" style={{ color: `${activeEra.primary}cc` }}>{activeEra.label}</span>
            ) : null}
          </div>
          {/* Globe — flex-1 so it claims all leftover middle space. The
              inner aspect-square box fills as much of that height as it
              can without being clipped, with a soft 24px inset margin so
              the sphere has air. */}
          <div className="flex-1 min-w-0 flex items-center justify-center overflow-hidden relative" data-testid="globe-panel">
            <div className="aspect-square" style={{
              height: 'min(calc(100% - 16px), calc(100vh - 320px))',
              maxHeight: 'min(calc(100% - 16px), calc(100vh - 320px))',
              maxWidth: 'min(calc(100% - 16px), calc(100vh - 320px))',
            }}>
              <MiniGlobe
                nodes={[]}
                beacons={[]}
                onNodeClick={onNodeClick || (() => {})}
                currentYear={currentYear}
                friendArcs={[]}
                activeUsers={presence.activeUsers}
                userHeat={presence.userHeat}
              />
              {/* Apr 30 2026 iter_66 per Lead: "Globe flight paths.
                  Starts at the users location doesn't move. Heat
                  signature only. There are no friends yet." Nodes,
                  beacons, and friendArcs pinned to empty arrays so
                  the only thing the sphere paints is the user's heat
                  pulse + the rotation. */}
            </div>
          </div>
          {/* Profile column — Apr 30 2026 iter_67 per Lead: "Profile
              emoji are off the page. Why?" The single-row flex-nowrap
              strip was wider than the 240px clamp min-width, so the
              last few icons clipped off-screen. Column widened to
              clamp(300px, 20vw, 380px), emoji strip padded so all 7
              stay visible without horizontal scroll. */}
          <div className="flex-shrink-0 flex flex-col items-center justify-center gap-2 px-5" style={{ width: 'clamp(300px, 20vw, 380px)' }} data-testid="hud-profile">
            <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full border-2 border-[#2F8A92]/35 bg-[#2F8A92]/10 flex items-center justify-center" data-testid="profile-avatar">
              <User size={32} className="text-[#2F8A92]" />
            </div>
            <span className="font-hud text-sm lg:text-base text-white/95 tracking-wider text-center max-w-[200px] truncate">{user?.name || user?.email}</span>
            <div className="flex items-center justify-center flex-nowrap gap-2.5 lg:gap-3 w-full" data-testid="profile-shortcuts">
              <button onClick={() => onViewChange('us')} className="text-white/85 hover:text-[#2F8A92] transition-all duration-300 flex-shrink-0" title="My Country"><Flag size={16} /></button>
              <button onClick={() => onViewChange('chat')} className="text-white/85 hover:text-[#2F8A92] transition-all duration-300 flex-shrink-0" title="Chat"><MessageCircle size={16} /></button>
              <button onClick={() => onViewChange('roots')} className="text-white/85 hover:text-[#D4A84A] transition-all duration-300 flex-shrink-0" title="My Roots"><TreePine size={16} /></button>
              <button onClick={() => onViewChange('workcenter')} className="text-white/85 hover:text-[#2F8A92] transition-all duration-300 flex-shrink-0" title="Class"><GraduationCap size={16} /></button>
              <button onClick={() => onViewChange('methodology-moderation')} data-testid="profile-methodology-btn" className="text-white/85 hover:text-[#D4A84A] transition-all duration-300 flex-shrink-0" title="Why Wasn't This Flagged?"><Sparkles size={16} /></button>
              <button onClick={() => onViewChange('profile')} data-testid="profile-settings-btn" className="text-white/85 hover:text-[#2F8A92] transition-all duration-300 flex-shrink-0" title="My Profile"><User size={16} /></button>
              <button onClick={() => onViewChange('accountability')} data-testid="profile-record-btn" className="text-white/85 hover:text-[#D4A84A] transition-all duration-300 flex-shrink-0" title="My Record"><Shield size={16} /></button>
            </div>
            <button onClick={logout} className="text-[#52525B] hover:text-[#2F8A92] transition-all duration-300 mt-1 flex items-center gap-1.5 font-hud text-[11px] tracking-widest" title="Logout">
              <LogOut size={12} /> LOGOUT
            </button>
          </div>
        </div>
      </div>
      )}

      {/* ═══ ROW 3: Timeline — full-width hero treatment, era labels readable.
           The "Start Your Journey" play button now lives ON the timeline
           itself, anchored to the bottom of the gold cursor line (per
           Lead's Apr 28 2026 brief). The redundant pill below was retired. ═══ */}
      {/* ═══ ROW 3: Chain Timeline ═══ Apr 30 2026 iter_66 per Lead:
           "Timeline could be much more smaller without losing effect."
           Row height trimmed 240/260 → 180/200 — still readable, gives
           the globe and the rest of the HUD their air back. */}
      {chrome !== 'tabs-only' && (
      <div className="pointer-events-auto flex-shrink-0 px-3 md:px-6 py-2 z-20 border-t border-[#2F8A92]/8" style={{ background: '#0A0A0A' }} data-testid="hud-row-timeline">
        <div className="h-[180px] md:h-[200px] overflow-visible">
          <ChainTimeline
            currentYear={currentYear || -7000000}
            onYearChange={onYearChange || (() => {})}
            events={timelineEvents || []}
            scopeRange={timelineScope}
            onChainSelect={onChainSelect}
            onStartJourney={() => {
              // Apr 30 2026 iter_69 per Lead: "When you click play it
              // goes to world history then into the episode. When you
              // press play it should go straight into the episode."
              // Navigate directly to /demo without the 260ms wait or
              // the intermediate view switch so the transition is
              // instant.
              onChainSelect?.({ origin: 'start-journey', year: currentYear });
              if (typeof window !== 'undefined') window.location.assign('/demo');
            }}
          />
        </div>
      </div>
      )}

      {/* ═══ CONTENT ═══ Suppressed in Time Machine mode so globe+timeline
           dominate the viewport. Rendered for every other tab / route.
           Apr 30 2026 — Lead's call: "Look at this picture does it look
           as pretty as yours. No its white and black hard on the eye."
           Reverting the harsh white content background back to the
           cinematic dark obsidian theme. Per-page components (About,
           Contact) handle their own typography on the dark canvas. */}
      {!isTimeMachine && (
        <div
          className="z-10"
          data-testid="hud-main-content"
          style={{ background: '#0A0A0A', color: '#E8E8E8' }}
        >
          {children}
        </div>
      )}

      {/* ═══ FOOTER ═══ */}
      <div className="pointer-events-auto flex-shrink-0 h-9 md:h-11 flex items-center justify-between px-3 md:px-8 z-20 border-t border-[#2F8A92]/8" style={{ background: '#0A0A0A' }} data-testid="hud-footer">
        {isTeacherOrAdmin ? (
          <div className="flex items-center gap-1.5" data-testid="footer-mode-toggle">
            <Shield size={11} className={isKidMode ? 'text-[#D4A84A]' : 'text-[#2F8A92]'} />
            <span className="font-hud text-[9px] md:text-[10px] tracking-wider" style={{ color: isKidMode ? '#D4A84A' : '#52525B' }}>KID</span>
            <button data-testid="kid-adult-toggle" onClick={handleAdultToggle} className="relative w-8 md:w-10 h-4 md:h-5 rounded-full transition-all duration-300" style={{ background: isKidMode ? 'rgba(212,168,74,0.25)' : 'rgba(47,138,146,0.25)' }}>
              <div className="absolute top-0.5 w-3 md:w-4 h-3 md:h-4 rounded-full transition-all duration-300" style={{ left: isKidMode ? '2px' : window.innerWidth < 768 ? '14px' : '22px', background: isKidMode ? '#D4A84A' : '#2F8A92' }} />
            </button>
            <span className="font-hud text-[9px] md:text-[10px] tracking-wider" style={{ color: !isKidMode ? '#2F8A92' : '#52525B' }}>ADULT</span>
          </div>
        ) : <div />}
        <span
          className="font-mono text-[9px] md:text-[10px] text-white/55 tracking-wider"
          title="LLC formation in progress — honest-date principle. The trade name will be the legal owner after California filing completes."
        >
          BUILT BY MIDNITE PRODUCTIONS · LLC FORMATION IN PROGRESS
        </span>
        <div className="flex items-center gap-3" data-testid="footer-right">
          {/* Apr 29 2026 follow-up #4: Lead's call: admin link moved from
              the top tab bar into the footer, small text, right-aligned. */}
          {isAdmin && (
            <button
              data-testid="footer-admin-link"
              onClick={() => onViewChange('admin')}
              className={`font-mono text-[9px] md:text-[10px] tracking-wider transition-colors ${activeView === 'admin' ? 'text-[#D4A84A]' : 'text-white/55 hover:text-[#D4A84A]'}`}
            >
              ADMIN
            </button>
          )}
          <div className="flex items-center gap-1.5" data-testid="footer-sound-toggle">
            <span className="font-hud text-[9px] md:text-[10px] text-white/80 tracking-wider hidden md:inline">SOUND</span>
            <button data-testid="sound-toggle" onClick={onToggleSound} className="text-white/80 hover:text-[#2F8A92] transition-all duration-300">
              {soundOn ? <Volume2 size={14} className="text-[#2F8A92]" /> : <VolumeX size={14} className="text-[#52525B]" />}
            </button>
          </div>
        </div>
      </div>

      {/* ═══ PIN Modal ═══ */}
      {showPinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.8)' }} data-testid="pin-modal-overlay">
          <div className="p-6 md:p-8 w-full max-w-sm mx-4 animate-fade-in border border-[#2F8A92]/15 rounded-lg" style={{ background: '#0A0A0A' }} data-testid="pin-modal">
            <div className="text-center mb-4">
              <Shield className="mx-auto text-[#D4A84A] mb-2" size={28} />
              <h3 className="font-historical text-[#D4A84A] text-lg">Parental Lock</h3>
              <p className="font-mono text-xs text-white/80 mt-1">Enter 4-digit PIN for Adult mode</p>
            </div>
            {pinError && <div className="mb-3 p-2 border border-red-400/30 bg-red-400/5 font-mono text-xs text-red-400 text-center" data-testid="pin-error">{pinError}</div>}
            <div className="flex justify-center gap-2 mb-4">
              {[0,1,2,3].map(i => (
                <div key={i} className="w-10 h-12 border-b-2 flex items-center justify-center font-hud text-xl" style={{ borderColor: pinInput.length > i ? '#2F8A92' : '#52525B', color: '#2F8A92' }}>
                  {pinInput[i] ? '\u2022' : ''}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 max-w-[180px] mx-auto mb-3">
              {[1,2,3,4,5,6,7,8,9,null,0,'del'].map((num, i) => {
                if (num === null) return <div key={i} />;
                return (
                  <button key={i} data-testid={`pin-key-${num}`}
                    onClick={() => {
                      if (num === 'del') { setPinInput(p => p.slice(0,-1)); setPinError(''); }
                      else if (pinInput.length < 4) {
                        const next = pinInput + String(num);
                        setPinInput(next);
                        setPinError('');
                        if (next.length === 4) {
                          setTimeout(() => {
                            if (next === (user?.parental_pin || '1234')) { setShowPinModal(false); setPinInput(''); onToggleMode(); }
                            else { setPinError('Incorrect PIN'); setPinInput(''); }
                          }, 200);
                        }
                      }
                    }}
                    className="h-9 rounded font-hud text-sm text-white/80 hover:text-[#2F8A92] hover:bg-[#2F8A92]/5 transition-all duration-300 border border-[#2F8A92]/10"
                  >{num === 'del' ? '\u232B' : num}</button>
                );
              })}
            </div>
            <button data-testid="pin-cancel" onClick={() => { setShowPinModal(false); setPinInput(''); setPinError(''); }} className="w-full font-hud text-xs tracking-wider text-white/80 hover:text-[#2F8A92] py-1.5">CANCEL</button>
          </div>
        </div>
      )}
      {/* Coming Soon gate — fires when the user clicks any tab from the
          COMING_SOON_TABS set or selects a country/state/city that
          doesn't yet have a published chain. */}
      <ComingSoonModal
        open={!!comingSoon}
        label={comingSoon?.label}
        onClose={() => setComingSoon(null)}
      />
    </div>
  );
}



/* ─── CascadeDropdown ───
 *
 * Lead Foundation Brief §1, §2: every nation / subdivision / city listed
 * from day one. Active entries are clickable; coming-soon entries are
 * visible but not clickable, so users see the lifetime of content ahead.
 * Empty-state copy stating intentional sequencing — not apologies.
 */
function CascadeDropdown({ kind, scope, countries, subdivisions, cities, onSelect, onClose }) {
  const [filter, setFilter] = useState('');

  let items = [];
  let label = 'Region';
  let needsParentScope = false;
  let parentMissingCopy = '';

  if (kind === 'country') {
    items = countries;
    label = 'Country';
  } else if (kind === 'subdivision') {
    if (!scope.country) {
      needsParentScope = true;
      parentMissingCopy = 'Pick a country first.';
    } else {
      items = subdivisions.items || [];
      label = subdivisions.subdivision_label || scope.country.subdivision_label || 'Region';
    }
  } else if (kind === 'city') {
    if (!scope.subdivision) {
      needsParentScope = true;
      parentMissingCopy = scope.country
        ? `Pick a ${(scope.country.subdivision_label || 'region').toLowerCase()} first.`
        : 'Pick a country first.';
    } else {
      items = cities.items || [];
      label = 'City';
    }
  }

  const filtered = filter
    ? items.filter((x) => (x.name || '').toLowerCase().includes(filter.toLowerCase()))
    : items;

  return (
    <div
      className="absolute left-0 top-full mt-1 w-[320px] md:w-[420px] max-h-[420px] overflow-hidden rounded-md backdrop-blur-md flex flex-col"
      data-testid={`cascade-dropdown-${kind}`}
      data-dropdown-anchor={kind}
      style={{
        background: 'rgba(10,10,10,0.97)',
        border: '1px solid rgba(47,138,146,0.18)',
        boxShadow: '0 16px 40px -10px rgba(0,0,0,0.85), 0 0 0 1px rgba(212,168,74,0.04)',
        zIndex: 50,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-2 border-b border-[#2F8A92]/10 flex items-center gap-2">
        <span className="font-hud text-xs text-[#D4A84A] tracking-widest uppercase">{label}</span>
      </div>
      <div className="flex-1 overflow-y-auto">
        {needsParentScope ? (
          <div className="px-4 py-6 font-mono text-[11px] md:text-xs text-white/65 leading-relaxed">
            {parentMissingCopy}
          </div>
        ) : filtered.length === 0 && items.length === 0 ? (
          <div className="px-4 py-6 font-mono text-[11px] md:text-xs text-white/65 leading-relaxed">
            {kind === 'country' && (
              <>Country histories will populate as the World History chronology reaches the migration of humans to these lands. We start where the story actually starts — humanity before its borders.</>
            )}
            {kind === 'subdivision' && (
              <>Subdivision content scales after country histories populate. Chronological depth first, geographic depth second.</>
            )}
            {kind === 'city' && (
              <>City content scales after subdivisions populate. The platform builds depth in the order history actually happened.</>
            )}
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-6 font-mono text-[11px] md:text-xs text-white/65">No matches for &quot;{filter}&quot;.</div>
        ) : (
          <ul className="py-1" data-testid={`cascade-list-${kind}`}>
            {filtered.map((item) => {
              const isPublished = item.chain_status === 'published';
              return (
                <li key={item.id || item.slug}>
                  <button
                    data-testid={`cascade-item-${kind}-${item.slug || item.id}`}
                    onClick={() => isPublished ? onSelect(item) : null}
                    disabled={!isPublished}
                    className={`w-full text-left px-4 py-2 flex items-center justify-between font-mono text-[12px] md:text-sm transition-colors ${
                      isPublished
                        ? 'text-white/90 hover:bg-[#2F8A92]/8 hover:text-[#2F8A92] cursor-pointer'
                        : 'text-white/35 cursor-not-allowed'
                    }`}
                  >
                    <span className="truncate">{item.name}</span>
                    {!isPublished && (
                      <span className="font-hud text-[9px] tracking-widest text-[#52525B] uppercase ml-2 flex-shrink-0">coming soon</span>
                    )}
                    {isPublished && (
                      <span className="font-hud text-[9px] tracking-widest text-[#D4A84A] uppercase ml-2 flex-shrink-0">walked</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="border-t border-[#2F8A92]/10 px-3 py-1.5 font-mono text-[9px] md:text-[10px] text-white/45 italic">
        The methodology decides through chain completion. The list grows by genuine work.
      </div>
    </div>
  );
}
